import {
  EnvironmentProviders,
  inject,
  InjectionToken,
  Provider,
  provideAppInitializer,
} from '@angular/core';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  Firestore,
  getFirestore,
} from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  Functions,
  getFunctions,
} from 'firebase/functions';
import {
  connectStorageEmulator,
  FirebaseStorage,
  getStorage,
} from 'firebase/storage';
import { Analytics, getAnalytics } from 'firebase/analytics';
import { FirebasePerformance, getPerformance } from 'firebase/performance';
import {
  AppCheck,
  initializeAppCheck,
  ReCaptchaV3Provider,
} from 'firebase/app-check';
import { AppCheckFallbackProvider } from './app-check-fallback.provider';

// Injection token for emulator configuration
export const USE_EMULATORS = new InjectionToken<boolean>('USE_EMULATORS');

// Injection token signaling that Performance Monitoring waits for analytics consent.
// Consumed by FirebaseAnalyticsHandler — when true, the SDK is booted from
// enableAnalytics() instead of an eager AppInitializer.
export const PERFORMANCE_REQUIRES_CONSENT = new InjectionToken<boolean>(
  'PERFORMANCE_REQUIRES_CONSENT',
);

// Firebase-SDK-Instanzen als InjectionTokens. Ersetzt die Klassen-Tokens von
// @angular/fire (inject(Firestore) etc.) — das Paket hat keinen Angular-22-
// Release und blockierte damit den Framework-Major.
// Muster: ~/.claude/learnings/firebase-native-sdk-angular.md
export const FIREBASE_APP = new InjectionToken<FirebaseApp>('FIREBASE_APP');
export const FIREBASE_AUTH = new InjectionToken<Auth>('FIREBASE_AUTH');
export const FIREBASE_FIRESTORE = new InjectionToken<Firestore>(
  'FIREBASE_FIRESTORE',
);
export const FIREBASE_FUNCTIONS = new InjectionToken<Functions>(
  'FIREBASE_FUNCTIONS',
);
export const FIREBASE_STORAGE = new InjectionToken<FirebaseStorage>(
  'FIREBASE_STORAGE',
);
export const FIREBASE_ANALYTICS = new InjectionToken<Analytics>(
  'FIREBASE_ANALYTICS',
);
export const FIREBASE_PERFORMANCE = new InjectionToken<FirebasePerformance>(
  'FIREBASE_PERFORMANCE',
);
export const FIREBASE_APP_CHECK = new InjectionToken<AppCheck>(
  'FIREBASE_APP_CHECK',
);

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

export interface FirebaseEmulatorConfig {
  useEmulators?: boolean;
  useFunctionsEmulatorOnly?: boolean;
}

export interface FirebaseFeatureConfig {
  enableAnalytics?: boolean;
  enableAppCheck?: boolean;
  enablePerformance?: boolean;
  /**
   * When true, the Firebase Performance SDK is not booted eagerly at app start.
   * Instead it boots after the user grants analytics consent
   * (via FirebaseAnalyticsHandler.enableAnalytics).
   */
  performanceRequiresConsent?: boolean;
  recaptchaSiteKey?: string;
  tier?: string;
  isTokenAutoRefreshEnabled?: boolean;
}

const FUNCTIONS_REGION = 'europe-west3';

export function provideFirebase(
  config: FirebaseConfig,
  emulatorConfig: FirebaseEmulatorConfig = {},
  featureConfig: FirebaseFeatureConfig = {},
): (Provider | EnvironmentProviders)[] {
  const { useEmulators = false, useFunctionsEmulatorOnly = false } =
    emulatorConfig;
  const {
    enableAnalytics = false,
    enableAppCheck = false,
    enablePerformance = false,
    performanceRequiresConsent = false,
    recaptchaSiteKey,
    tier = 'UNKNOWN',
    isTokenAutoRefreshEnabled = true,
  } = featureConfig;

  const providers: (Provider | EnvironmentProviders)[] = [
    {
      provide: FIREBASE_APP,
      // initializeApp() wirft, wenn die Default-App schon existiert (Tests,
      // HMR). Vorhandene App wiederverwenden statt neu zu initialisieren.
      useFactory: () => (getApps().length ? getApp() : initializeApp(config)),
    },

    {
      provide: FIREBASE_AUTH,
      useFactory: (app: FirebaseApp) => {
        const auth = getAuth(app);
        if (useEmulators && !useFunctionsEmulatorOnly) {
          try {
            connectAuthEmulator(auth, 'http://localhost:9139', {
              disableWarnings: true,
            });
            console.log('Connected to Auth emulator on localhost:9139');
          } catch (error) {
            console.error('❌ FAILED to connect to Auth emulator:', error);
            console.error(
              'This means login will fail! Check if emulator is running.',
            );
          }
        }
        return auth;
      },
      deps: [FIREBASE_APP],
    },

    {
      provide: FIREBASE_FIRESTORE,
      useFactory: (app: FirebaseApp) => {
        const firestore = getFirestore(app);
        if (useEmulators && !useFunctionsEmulatorOnly) {
          try {
            connectFirestoreEmulator(firestore, 'localhost', 8120);
            console.log('Connected to Firestore emulator on localhost:8120');
          } catch (error) {
            console.warn('Failed to connect to Firestore emulator:', error);
          }
        }
        return firestore;
      },
      deps: [FIREBASE_APP],
    },

    {
      provide: FIREBASE_FUNCTIONS,
      // Die Region ist beim nativen SDK ein Parameter von getFunctions() —
      // @angular/fire setzte sie nachträglich als Property (functions.region).
      useFactory: (app: FirebaseApp) => {
        const functions = getFunctions(app, FUNCTIONS_REGION);
        if (useEmulators || useFunctionsEmulatorOnly) {
          try {
            connectFunctionsEmulator(functions, 'localhost', 5041);
            console.log('Connected to Functions emulator on localhost:5041');
          } catch (error) {
            console.warn('Failed to connect to Functions emulator:', error);
          }
        }
        return functions;
      },
      deps: [FIREBASE_APP],
    },

    {
      provide: FIREBASE_STORAGE,
      useFactory: (app: FirebaseApp) => {
        const storage = getStorage(app);
        if (useEmulators && !useFunctionsEmulatorOnly) {
          try {
            connectStorageEmulator(storage, 'localhost', 9239);
            console.log('Connected to Storage emulator on localhost:9239');
          } catch (error) {
            console.warn('Failed to connect to Storage emulator:', error);
          }
        }
        return storage;
      },
      deps: [FIREBASE_APP],
    },
  ];

  // Conditionally add Analytics (browser-only — not available during SSR).
  // Bleibt lazy: die Factory läuft erst, wenn FirebaseAnalyticsHandler das
  // Token nach erteiltem Consent injiziert.
  if (enableAnalytics && config.measurementId && typeof window !== 'undefined') {
    providers.push({
      provide: FIREBASE_ANALYTICS,
      useFactory: (app: FirebaseApp) => getAnalytics(app),
      deps: [FIREBASE_APP],
    });
  }

  // Conditionally add Performance Monitoring (browser-only — not available during SSR).
  // Kein Consumer injiziert FIREBASE_PERFORMANCE — ohne den Initializer liefe
  // die Factory nie und das SDK bootet nicht. Bei consent-gated Apps startet
  // FirebaseAnalyticsHandler.enableAnalytics() es stattdessen.
  if (enablePerformance && typeof window !== 'undefined') {
    providers.push(
      {
        provide: FIREBASE_PERFORMANCE,
        useFactory: (app: FirebaseApp) => getPerformance(app),
        deps: [FIREBASE_APP],
      },
      {
        provide: PERFORMANCE_REQUIRES_CONSENT,
        useValue: performanceRequiresConsent,
      },
    );
    if (!performanceRequiresConsent) {
      providers.push(
        provideAppInitializer(() => {
          inject(FIREBASE_PERFORMANCE);
        }),
      );
    }
  }

  // Conditionally add App Check (browser-only — reCAPTCHA requires DOM)
  if (enableAppCheck && !useEmulators && typeof window !== 'undefined') {
    // Enable debug token mode on localhost so developers can register the
    // token in the Firebase console instead of getting a 403 error.
    // Preserve an existing token (e.g. injected by Playwright via addInitScript).
    if (window.location.hostname === 'localhost') {
      const existing = (self as unknown as Record<string, unknown>)[
        'FIREBASE_APPCHECK_DEBUG_TOKEN'
      ];
      if (!existing) {
        (self as unknown as Record<string, unknown>)[
          'FIREBASE_APPCHECK_DEBUG_TOKEN'
        ] = true;
      }
    }

    providers.push(
      {
        provide: FIREBASE_APP_CHECK,
        useFactory: (app: FirebaseApp) => {
          try {
            if (!recaptchaSiteKey) {
              console.warn('App Check: reCAPTCHA site key is missing');
              throw new Error('reCAPTCHA site key is required for App Check');
            }

            // Primärpfad bleibt reCAPTCHA v3. Der Fallback-Provider legt nur
            // einen serverseitigen Rückfallweg für angemeldete Nutzer darüber,
            // falls reCAPTCHA im Browser kein Token liefert (Tracking-Schutz,
            // Adblocker, Firewall, schlechter Empfang). Ohne ihn ist die App bei
            // erzwungenem Firestore-Enforcement für Betroffene unbenutzbar.
            // Scheitert die Konstruktion — etwa weil ein SDK-Update die internen
            // Provider-Methoden umbenennt —, läuft die App unverändert auf dem
            // reinen reCAPTCHA-Pfad weiter, statt gar nicht zu starten.
            let provider: ReCaptchaV3Provider | AppCheckFallbackProvider;

            try {
              provider = new AppCheckFallbackProvider({
                recaptchaSiteKey,
                appId: config.appId,
                projectId: config.projectId,
              });
            } catch (fallbackError) {
              console.warn(
                'App Check: Fallback-Provider nicht verfügbar, nutze reCAPTCHA allein',
                fallbackError,
              );
              provider = new ReCaptchaV3Provider(recaptchaSiteKey);
            }

            const appCheck = initializeAppCheck(app, {
              provider,
              // FIREBASE APP CHECK TOKEN AUTO-REFRESH
              //
              // AKTUELL: true (automatische Token-Refreshes alle ~55 Min)
              // ⚠️ KOSTEN: ~310€/Monat bei Vollausbau (68% durch Auto-Refresh)
              //    → 60% aller Assessments sind nur Token-Refreshes bei inaktiven Tabs
              //
              // THEORETISCHE OPTIMIERUNG: isTokenAutoRefreshEnabled: false
              // ✅ VORTEIL: ~68% Kosteneinsparung (€310/Monat bei Vollausbau)
              // ❌ RISIKO: HOCH - Dokumentierte Firebase SDK-Probleme:
              //    • Token-Refresh nach App-Neustart funktioniert nicht (GitHub #5235)
              //    • Services erkennen neue Tokens nicht ohne Neustart (GitHub #12799)
              //    • 403-Fehler bei langen Sessions (>60 Min)
              //    • Potentieller Datenverlust bei Formular-Submissions
              //    • Erhöhte Support-Kosten (geschätzt €8.320/Jahr)
              //
              // 🔴 NICHT EMPFOHLEN ohne 8-12 Wochen Testing (Dev → Staging → Canary)
              // 🔴 Break-Even bei ~4-8 Jahren (Implementierung vs. Einsparung)
              //
              // ALTERNATIVE OPTIMIERUNGEN (empfohlen):
              // • Website App Check für öffentliche Seiten deaktivieren (€8-13/Monat)
              // • Caching-Strategien implementieren (10-20% Einsparung)
              // • Alternative Provider evaluieren ab €300/Monat (Friendly Captcha, hCaptcha)
              //
              // Siehe: docs/architecture/APP_CHECK_FIRESTORE_RISIKO_ANALYSE.md (Kapitel 11.7)
              // Siehe: docs/architecture/APP_CHECK_KOSTENBERECHNUNG_2025-2028.md
              isTokenAutoRefreshEnabled,
            });

            console.log('App Check initialized successfully');
            return appCheck;
          } catch (error) {
            console.error('App Check initialization failed:', {
              error,
              tier,
              projectId: config.projectId,
              hasSiteKey: !!recaptchaSiteKey,
              errorMessage:
                error instanceof Error ? error.message : String(error),
            });

            throw new Error(
              `App Check initialization failed for ${tier}: ${
                error instanceof Error ? error.message : 'Unknown error'
              }`,
            );
          }
        },
        deps: [FIREBASE_APP],
      },
      // App Check muss vor dem ersten Firestore-/Functions-Zugriff stehen.
      // @angular/fire bootete provideAppCheck() eager; beim nativen SDK
      // erzwingt das dieser Initializer.
      provideAppInitializer(() => {
        inject(FIREBASE_APP_CHECK);
      }),
    );
  }

  return providers;
}
